// A game's state slots between sessions, in localStorage: which slots it
// has, which is active, their names, and what the battery save said when
// each was last written.

export interface SlotStats {
  /** Epoch ms when the state was written. */
  at: number;
  trainer: string | null;
  playTime: { hours: number; minutes: number } | null;
}

const activeKey = (rom: string) => `pocket-play.slot.${rom}`;
const listKey = (rom: string) => `pocket-play.slots.${rom}`;
const nameKey = (rom: string, slot: number) => `pocket-play.slotName.${rom}.${slot}`;
const statsKey = (rom: string, slot: number) => `pocket-play.slotStats.${rom}.${slot}`;

export const slotStore = {
  active(rom: string): number | null {
    const n = Number(localStorage.getItem(activeKey(rom)));
    return n >= 1 ? n : null;
  },
  setActive(rom: string, slot: number) {
    localStorage.setItem(activeKey(rom), String(slot));
  },
  clearActive(rom: string) {
    localStorage.removeItem(activeKey(rom));
  },
  /** Registered slots, ascending; slot 1 when nothing is stored. */
  list(rom: string): number[] {
    const raw = localStorage.getItem(listKey(rom));
    const slots = raw ? (JSON.parse(raw) as number[]) : [];
    return slots.length ? slots : [1];
  },
  setList(rom: string, slots: number[]) {
    localStorage.setItem(listKey(rom), JSON.stringify(slots));
  },
  /** Whether the game has ever had slots set up (a game whose slots were all removed has an empty list stored). */
  known(rom: string): boolean {
    return localStorage.getItem(listKey(rom)) !== null || localStorage.getItem(activeKey(rom)) !== null;
  },
  names(rom: string, slots: number[]): Record<number, string> {
    const names: Record<number, string> = {};
    for (const slot of slots) {
      const name = localStorage.getItem(nameKey(rom, slot));
      if (name) names[slot] = name;
    }
    return names;
  },
  rename(rom: string, slot: number, name: string) {
    if (name) localStorage.setItem(nameKey(rom, slot), name);
    else localStorage.removeItem(nameKey(rom, slot));
  },
  stats(rom: string, slots: number[]): Record<number, SlotStats> {
    const stats: Record<number, SlotStats> = {};
    for (const slot of slots) {
      const raw = localStorage.getItem(statsKey(rom, slot));
      if (raw) stats[slot] = JSON.parse(raw) as SlotStats;
    }
    return stats;
  },
  setStats(rom: string, slot: number, stats: SlotStats) {
    localStorage.setItem(statsKey(rom, slot), JSON.stringify(stats));
  },
  /** Carries a game's slots over to a new file name, unless that name already has slots. */
  move(from: string, to: string) {
    if (slotStore.known(to)) return;
    const slots = slotStore.list(from);
    const keys = [activeKey, listKey].map((key) => [key(from), key(to)]);
    for (const slot of slots)
      keys.push([nameKey(from, slot), nameKey(to, slot)], [statsKey(from, slot), statsKey(to, slot)]);
    for (const [old, next] of keys) {
      const value = localStorage.getItem(old);
      if (value === null) continue;
      localStorage.setItem(next, value);
      localStorage.removeItem(old);
    }
  },
  /** Drops a slot's name, stats and active status; the list is the caller's. */
  forget(rom: string, slot: number) {
    localStorage.removeItem(nameKey(rom, slot));
    localStorage.removeItem(statsKey(rom, slot));
    if (slotStore.active(rom) === slot) slotStore.clearActive(rom);
  },
};

/**
 * Whether a state written at `savedAt` still reflects what the player sees:
 * nothing has been pressed since, it is younger than one auto-save
 * interval, or it was written after the game stopped advancing.
 */
export function stateIsFresh(
  savedAt: Date,
  lastInputAt: number,
  idleSince: Date | null,
  autoSaveMs: number,
  now = Date.now(),
): boolean {
  return (
    savedAt.getTime() >= lastInputAt ||
    now - savedAt.getTime() < autoSaveMs ||
    (idleSince !== null && savedAt >= idleSince)
  );
}
